import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface UseFadeUpOnScrollOptions {
  /** CSS selector for child elements to animate. Default '[data-anim]'. */
  selector?: string;
  /** Stagger between children in seconds. Default 0.12. */
  stagger?: number;
}

/**
 * Plays a staggered fade-up entrance on every matching child inside `root`
 * whenever the nearest section (or stickySlot wrapper) has `data-active="true"`.
 *
 * The `data-active` attribute is set by `useSectionActive` (scroll-driven on
 * desktop, statically on mount on mobile). This hook reacts to that signal.
 *
 * - Animations replay every time the section becomes active again.
 * - `prefers-reduced-motion`: skips the transform, sets opacity immediately.
 * - All timing is CSS-only — no GSAP, no JS animation loop.
 *
 * **Mobile fallback:** `useSectionActive` sets `data-active="true"` on every
 * slot synchronously at mount, before this hook's MutationObserver attaches,
 * so we never see the mutation. We snapshot the initial active state and play
 * immediately if it's already on.
 */
export const useFadeUpOnScroll = <T extends HTMLElement | null>(
  ref: RefObject<T>,
  options: UseFadeUpOnScrollOptions = {},
): void => {
  const { prefersReducedMotion } = useReducedMotion();
  const stagger = options.stagger ?? 0.12;
  const selector = options.selector ?? '[data-anim]';
  const activeRef = useRef(false);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const targets = Array.from(root.querySelectorAll<HTMLElement>(selector));
    if (targets.length === 0) return;

    const play = (): void => {
      if (activeRef.current) return;
      activeRef.current = true;

      if (prefersReducedMotion) {
        targets.forEach((el) => {
          el.classList.add('is-visible');
          el.style.transitionDelay = '';
          el.style.opacity = '1';
          el.style.transform = 'none';
        });
        return;
      }

      targets.forEach((el, idx) => {
        el.classList.remove('is-visible');
        el.style.transitionDelay = '';
        // Force reflow so the next add re-triggers the transition.
        void el.offsetHeight;
        el.style.transitionDelay = `${idx * stagger}s`;
        el.classList.add('is-visible');
      });
    };

    const reset = (): void => {
      if (!activeRef.current) return;
      activeRef.current = false;
      targets.forEach((el) => {
        el.classList.remove('is-visible');
        el.style.transitionDelay = '';
      });
    };

    // Watch the closest section OR stickySlot for data-active changes. The
    // HorizontalPager sets data-active on the slot wrapper (for stacking
    // sections) or directly on the section (for the cover and on mobile).
    const target: HTMLElement | null = root.closest('section') ?? root.closest('[data-active]');

    if (!target) {
      // Fallback: section wrapper isn't inside a pager (e.g. Login page).
      // Animate immediately on mount so content isn't stuck invisible.
      play();
      return;
    }

    const observer = new MutationObserver(() => {
      if (target.dataset.active === 'true') {
        play();
      } else {
        reset();
      }
    });

    observer.observe(target, {
      attributes: true,
      attributeFilter: ['data-active'],
    });

    // Snapshot initial state — on mobile the flag is set synchronously at
    // mount before this observer attaches, so we play right away.
    if (target.dataset.active === 'true') play();

    return () => {
      observer.disconnect();
      targets.forEach((el) => {
        el.style.transitionDelay = '';
      });
    };
  }, [ref, selector, prefersReducedMotion, stagger]);
};
